// Scheduled Reports: what an answer from the reports service means for a page. Pure, so Jest covers it.
// A plain 403 is a token renewal the shared request layer handles; if one still reaches a page it reads
// "Please reload the page", never a denied state. None of these signs the user out.
import { isLostAnswer } from "utils/shareTable";

const statusOf = (error) => {
  if (!error) return undefined;
  return typeof error.status === "number" ? error.status : error.originalStatus;
};

const noRoute = (error) => {
  const status = statusOf(error);
  return (
    (error.data && error.data.errorCode === "NO_ROUTE") ||
    status === 404 ||
    (error.status === "PARSING_ERROR" && status < 500)
  );
};

// The four outcomes of GET /reports/config (plan 4.9). A later failed reload keeps the answer already in
// hand, so a token renewal never unmounts an open form.
export function configOutcome({ data, error, isUninitialized, isLoading } = {}) {
  const payload = data && data.payload;
  if (payload) {
    return { outcome: payload.available === true ? "available" : "notAvailable", config: payload };
  }
  if (error) {
    if (noRoute(error)) return { outcome: "notAvailable", config: null };
    const status = statusOf(error);
    if (error.status === "FETCH_ERROR" || error.status === "TIMEOUT_ERROR" || status >= 500) {
      return { outcome: "paused", config: null };
    }
    return { outcome: "error", config: null };
  }
  if (isLoading || isUninitialized || !data) return { outcome: "loading", config: null };
  return { outcome: "notAvailable", config: null };
}

const DENIED_WORDS = {
  licence: "Your licence does not allow this.",
  "not available here": "Scheduled Reports is not available here.",
  access: "Your role does not allow this.",
  "operator pause": "Invixible has paused this report. Please contact Invixible support.",
  scope: "You do not hold every solution of this report in full.",
};

// { kind, message, reason?, fieldErrors?, serviceIds? } for any refused or lost call.
export function classifyReportError(error, { preview = false } = {}) {
  const data = (error && error.data) || {};
  const status = statusOf(error);
  if (data.errorCode === "REPORT_NOT_FOUND") {
    return { kind: "notFound", message: "This report does not exist, or you cannot see it." };
  }
  if (data.errorCode === "REPORT_VERSION_CONFLICT" || status === 409) {
    return {
      kind: "conflict",
      message:
        "Someone else changed this report after you opened it, so your changes were not saved. Your changes are still on this page. Reload shows the report as it is now; your unsaved changes are not kept.",
    };
  }
  if (status === 403 && data.errorCode === "SCOPE_DENIED") {
    // the service words a mail-limit refusal itself, with the numbers it may show
    const message =
      (data.reason !== "mail limit" && DENIED_WORDS[data.reason]) || data.message || "You cannot do this here.";
    return { kind: "denied", reason: data.reason, message, serviceIds: (data.payload && data.payload.serviceIds) || [] };
  }
  if (status === 403) return { kind: "reload", message: "Please reload the page." };
  if (status === 400 && Array.isArray(data.errors)) {
    return { kind: "validation", message: "Please correct the marked fields.", fieldErrors: fieldErrorsFrom(data.errors) };
  }
  if (status === 429) return { kind: "rateLimited", message: "Too many requests, please wait a moment and try again." };
  if (error && noRoute(error)) return { kind: "notAvailable", message: "This is not available here." };
  if (isLostAnswer(error)) {
    return {
      kind: "lost",
      message: preview ? "Still working, try again in a minute." : "No answer came back. Please check your connection and try again.",
    };
  }
  if (status >= 500) return { kind: "service", message: data.message || "The service had a problem, try again." };
  return { kind: "unknown", message: data.message || "Something went wrong, please try again." };
}

// The paths the form shows beside a field.
const PLACED = [/^name$/, /^scope(\.|$)/, /^sections$/, /^sections\.\d+\.options\./, /^schedule\.(sendAt|timeZone)$/, /^recipients(\.|$)/];

// { path: message } from the service's errors[]; any other path keeps its words in `_other`, so it still
// reaches the screen.
export function fieldErrorsFrom(errors) {
  const out = {};
  const other = [];
  (errors || []).forEach(({ path, message }) => {
    if (PLACED.some((re) => re.test(String(path)))) out[path] = message;
    else other.push(path ? `${path}: ${message}` : message);
  });
  if (other.length) out._other = other;
  return out;
}

// The first message at this path or below it.
export function fieldError(errors, path) {
  if (!errors) return "";
  const key = Object.keys(errors).find((k) => k === path || k.startsWith(`${path}.`));
  return key ? errors[key] : "";
}
